import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';

import { TOAST } from 'constant';
import { toast } from 'layout';
import { mapError } from 'utils/func';
import Loading from 'components/Loading';

const ResearchKpiConfirm = forwardRef(({ isLoading, refetch }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [confirmationControl, setConfirmationControl] = useState(null);
  const { mutate, mode, value } = useMemo(
    () => ({
      mode: confirmationControl?.mode ?? 'delete',
      mutate: confirmationControl?.mutate ?? null,
      value: confirmationControl?.value ?? { id: null },
    }),
    [confirmationControl]
  );

  // #endregion

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_confirmationControl) => {
        setConfirmationControl({ ..._confirmationControl });
        setVisible(true);
      },
    }),
    []
  );

  const footerConfirmationSubmit = () => (
    <div>
      <Button
        label={t('researchKpi.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setVisible(false)}
        autoFocus
      />
      <Button
        label={t('researchKpi.ok')}
        className="w-7rem"
        severity="info"
        onClick={() => {
          if (mode !== 'reorder' && !value?.id) return;
          const params = mode !== 'reorder' ? value?.id : value;

          mutate(params, {
            onSuccess: () => {
              toast(TOAST.SUCCESS, t(`researchKpi.${mode}.success`));
              setVisible(false);
              refetch();
            },
            onError: (error) => {
              toast(TOAST.ERROR, mapError(t, error));
            },
          });
        }}
      />
    </div>
  );
  // #endregion

  return (
    <Dialog
      header={<p className="text-blue-500">{t(`researchKpi.${mode}.title`)}</p>}
      visible={visible}
      position="center"
      style={{ width: '400px' }}
      onHide={() => setVisible(false)}
      draggable={false}
      footer={footerConfirmationSubmit}
    >
      {isLoading && <Loading />}
      <p className="text-lg font-bold">{value?.name}</p>
      <p className="text-lg">{t(`researchKpi.${mode}.confirmationMessage`)}</p>
    </Dialog>
  );
});

export default React.memo(ResearchKpiConfirm);

ResearchKpiConfirm.propTypes = {
  refetch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

ResearchKpiConfirm.defaultProps = {
  isLoading: false,
};
